import { useCallback } from 'react';
import { Node } from 'reactflow';
import { getFocusOnState } from '@components/MainStage/focusOn.store';
import useExpandCollapsedLayers from '@components/MainStage/hooks/useExpandCollapsedLayers';
import {
  getListSearchState,
  SEARCH_MIN_LENGTH,
} from '@components/MainStage/listSearch.store';
import {
  getMultiSelectionState,
  useMultiSelectionActions,
} from '@components/MainStage/multiSelection.store';
import { canvasState } from '@components/MainStage/store';
import { NodesTypes } from '@constants/canvas/general';
import { useProject } from '@context/Project';
import { isElementLocal } from '@utils/drawers';
import { matchesSearch } from '@utils/searchHighlight';

/**
 * Shift-click multi-selection entry / continuation logic shared by the canvas
 * and the entities list.
 *
 * - Entering multi-selection from a single opened entity (drawer open):
 *   closes the drawer following the unsaved-changes handling, then seeds the
 *   multi-selection with the previously opened entity (the anchor) and the
 *   shift-clicked one.
 * - Entering multi-selection by holding Shift before clicking: starts the
 *   multi-selection immediately on the first shift-click.
 * - Continuing to build: adds a not-yet-selected entity, or removes it if it is
 *   already part of the selection (deselect behaviour).
 *
 * Multi-selection is driven purely by our own store, so on every shift-click we
 * also clear React Flow's built-in single selection and the single-entity focus
 * dim to keep both models from fighting each other.
 */
const useMultiSelectEntity = () => {
  const { setSelection, setAnchor, toggle, clear } = useMultiSelectionActions();
  const { drawer, handleCloseDrawer } = useProject();
  const { expandAll } = useExpandCollapsedLayers();

  const selectEntity = useCallback(
    async (nodeId: string) => {
      // Never multi-select unsaved (local) entities.
      if (isElementLocal(nodeId)) return;

      // Focus On active: only entities inside the focused set may be selected.
      const focus = getFocusOnState();
      if (focus.sourceId && !focus.isFocused(nodeId)) return;

      const isMultiSelectionActive =
        getMultiSelectionState().selectedIds.length > 0;

      if (isMultiSelectionActive) {
        // Continuing an active multi-selection: add or deselect.
        toggle(nodeId);
      } else {
        // Entering multi-selection. The anchor (entity opened in the drawer) is
        // only relevant while a drawer is open.
        const anchorId = drawer ? getMultiSelectionState().anchorId : null;

        // Follow the unsaved-changes handling before leaving the single entity.
        if (drawer) {
          try {
            await handleCloseDrawer();
          } catch {
            // User cancelled the discard-changes confirmation.
            return;
          }
        }

        const seedIds =
          anchorId && anchorId !== nodeId && !isElementLocal(anchorId)
            ? [anchorId, nodeId]
            : [nodeId];

        setSelection(seedIds);
      }

      // Drop React Flow's single selection + the focus dim so the multi-select
      // highlight is the only active visual state.
      canvasState().unselectActiveNode();
      canvasState().unselectActiveEdge();
    },
    [drawer, handleCloseDrawer, setSelection, toggle],
  );

  /**
   * Cmd/Ctrl+A: adds every entity in the CURRENT RESULTING SUBSET to the
   * existing multi-selection (union — never removes or replaces entities that
   * fall outside the subset). The subset is the set of canvas entities that are:
   *   - real, saved entities (CustomNode, not local),
   *   - visible under the active scope / basic / questionnaire filters,
   *   - matching the active list search (when a search needle is present),
   *   - inside the focused set (when Focus On is active).
   * If the subset is empty (e.g. a search that matches nothing) it is a no-op
   * and the existing selection is preserved.
   *
   * A drawer open means a single entity is being viewed/edited: the
   * unsaved-changes handling is followed before switching to the union.
   */
  const selectAll = useCallback(
    async (filteredNodes: Node[]) => {
      const focus = getFocusOnState();

      const { query } = getListSearchState();
      const trimmedQuery = query.trim();
      const needle =
        trimmedQuery.length >= SEARCH_MIN_LENGTH
          ? trimmedQuery.toLowerCase()
          : '';

      const subsetIds = filteredNodes
        .filter((node) => {
          if (node.type !== NodesTypes.CustomNode || isElementLocal(node.id)) {
            return false;
          }

          // Hidden by the active scope / basic / questionnaire filters.
          if (node.hidden) return false;

          // Focus On active: only entities inside the focused set.
          if (focus.sourceId && !focus.isFocused(node.id)) return false;

          // Search active: only entities whose name matches the needle.
          if (needle && !matchesSearch(node.data.dto.label, needle)) {
            return false;
          }

          return true;
        })
        .map((node) => node.id);

      // Empty subset (e.g. search matches nothing): keep the existing selection.
      if (subsetIds.length === 0) return;

      // A drawer open means a single entity is being viewed/edited: follow the
      // unsaved-changes handling before switching to the full selection.
      if (drawer) {
        try {
          await handleCloseDrawer();
        } catch {
          // User cancelled the discard-changes confirmation.
          return;
        }
      }

      // Reveal every collapsed layer so all selected entities are visible.
      await expandAll();

      canvasState().unselectActiveNode();
      canvasState().unselectActiveEdge();

      // Union with the existing selection; setSelection de-dupes.
      const existingIds = getMultiSelectionState().selectedIds;
      setSelection([...existingIds, ...subsetIds]);
    },
    [drawer, handleCloseDrawer, setSelection, expandAll],
  );

  return {
    selectEntity,
    selectAll,
    clearMultiSelection: clear,
    setSingleSelectionAnchor: setAnchor,
  };
};

export default useMultiSelectEntity;
